import uuid from 'uuid-random'
import { useMainStore } from '@/store/main'

/**
 * CRM email links carry the user's ORIGINAL session_id as
 * `?from=email&session_id=<uuid>` (HIPAA: only session_id is allowed in the URL).
 * The Kova app opens a near-copy of the CRM intake link in its webview with `from=app`
 * instead of `from=email` (DEV-35414) — same session capability, so the id is adopted
 * from both sources; `isCrmFlow` (checkout email-edit, med-crm re-sync) stays
 * email-only. `from` is always a single value.
 * This is the single place that reads those params; adopting the id lets analytics
 * (onboarding_session_id), Sentry (user.id) and the med-* backends correlate the
 * return visit with the original onboarding session.
 *
 * Re-activation links additionally carry `?auth=login` (DEV-34178: the account already
 * exists) or `?auth=signup` (DEV-34293: the user paid but has no account yet). **The
 * funnel no longer reads that flag at all** — DEV-35018 removed Auth0 and /signin, so
 * every cohort walks the same screens and the account is always created server-side by
 * the pre-auth v3 signup (which is idempotent, so it is safe to fire for an existing
 * account too). CRM templates still send `auth=`; it is simply ignored here. Do not
 * re-add a branch on it without re-reading docs/crm-email-links.md — the previous one
 * silently mis-routed anyone whose per-tab flag was lost to an in-app-browser handoff.
 * The full CRM link taxonomy lives in src/views/Redirect.vue.
 */

/** Link sources whose session_id we adopt: CRM emails and the Kova app's webview links. */
const SESSION_SOURCES = ['email', 'app']

interface CrmSession {
	sessionId: string
	/** True for CRM mailings only — the app's webview link is not a CRM flow. */
	isCrmFlow: boolean
}

/** Pure: the valid CRM session id from a query string, or null.
 * Module-local (no test runner to import it; the repo's import/no-unused-modules
 * forbids unused exports). Logic stays isolated and pure for a future unit test. */
function parseCrmSession(search: string): CrmSession | null {
	const params = new URLSearchParams(search)
	const from = params.get('from')
	if (!from || !SESSION_SOURCES.includes(from)) return null
	const sessionId = params.get('session_id')
	// uuid-random.test() validates a v4 UUID — the exact shape this app generates
	// (store/main.js `sessionId: uuid()`), so it both parses and guards against junk.
	if (!sessionId || !uuid.test(sessionId)) return null
	return { sessionId, isCrmFlow: from === 'email' }
}

/**
 * Adopt the CRM session_id from the current URL into the main store and flag the visit as
 * CRM traffic. No-op for non-CRM loads. MUST run after Pinia is active and BEFORE
 * analytics/Sentry seed their context (wired in main.ts). The URL value wins over any
 * previously generated/persisted id — that is how we "recognise" the user.
 */
export function adoptCrmSessionFromUrl(search: string = window.location.search): void {
	const crmSession = parseCrmSession(search)
	if (!crmSession) return
	const mainStore = useMainStore()
	mainStore.sessionId = crmSession.sessionId
	mainStore.isCrmFlow = crmSession.isCrmFlow
}
